import React, { useContext, useMemo } from 'react';

import { Label } from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { CellProps, Column } from 'react-table';

import { ClientsAvatar } from 'components';
import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { IRole, IWorker, TRoleName, TWorker } from 'types';

import s from './WorkersTable.module.scss';

export const useGetColumns = (): Column<TWorker>[] => {
  const size = useWindowResize();
  const { t, lang } = useContext(LocalizationContext);

  return useMemo(() => {
    const baseColumns: Column<TWorker>[] = [
      {
        Header: t('worker'),
        accessor: ({
          last_name,
          first_name,
          phone,
          nickname,
          avatar_url,
          roles,
        }) => ({
          workerFIO: `${last_name} ${first_name?.[0]}.`,
          phone,
          nickname,
          avatar_url,
          roles,
        }),
        Cell: ({
          value,
        }: CellProps<{
          phone: string;
          workerFIO: string;
          nickname: string;
          avatar_url: string;
          roles: IRole[];
        }>) => (
          <div className={s.worker}>
            <ClientsAvatar
              avatar={value.avatar_url}
              roles={value.roles}
              size="s"
            />
            <div className={s.info_block}>
              <div className={s.name_block}>
                <span className={s.name}>{value.workerFIO}</span>
                {size.width < 960 && (
                  <span className={s.nickname}>{value.nickname}</span>
                )}
              </div>

              <span className={s.phone}>+{value.phone}</span>
            </div>
          </div>
        ),
        width: 220,
        disableResizing: true,
      },
      ...(size.width > 960
        ? [
            {
              Header: t('client.nickname'),
              accessor: 'nickname' as const,
              Cell: ({ value }: { value: string }) => <span>{value}</span>,
              width: 192,
              disableResizing: true,
            },
          ]
        : []),
      {
        Header: t('worker.role'),
        accessor: ({ roles, disabled_at, banned_at }) => {
          if (!roles) return { role: 'other' };

          const currentRole = roles.filter(({ id }) => id !== 4)[0];

          return {
            role: currentRole.alias,
            worker_status: t(
              disabled_at ? 'worker.status.locked' : 'worker.status.working'
            ),
            client_status: t(
              banned_at ? 'worker.status.canPlay' : 'worker.status.canPlay.no'
            ),
            disabled_at,
            banned_at,
          };
        },
        Cell: ({
          value,
        }: CellProps<{
          role: TRoleName;
          worker_status: string;
          client_status: string;
          disabled_at: boolean | null;
          banned_at: boolean | null;
        }>) => (
          <div className={s.role}>
            <Label text={t(`roles.${value.role as TRoleName}`)} rounded />
            {size.width < 960 && (
              <>
                <Label
                  color={value.disabled_at ? 'red' : 'green'}
                  iconName={!value.disabled_at ? 'SuccessIcon' : 'ErrorIcon'}
                  rounded
                />
                <Label
                  color={value.banned_at ? 'red' : 'green'}
                  iconName="ConsoleIcon"
                  rounded
                />
              </>
            )}
          </div>
        ),
        width: 275,
        sortType: (row1, row2, columId) => {
          const role1 = t(`roles.${row1.values[columId].role as TRoleName}`);
          const role2 = t(`roles.${row2.values[columId].role as TRoleName}`);
          return role1.localeCompare(role2, lang);
        },
      },
      ...(size.width > 960
        ? [
            {
              Header: t('status'),
              accessor: ({ disabled_at, banned_at }: TWorker) => ({
                worker_status: t(
                  disabled_at ? 'worker.status.locked' : 'worker.status.working'
                ),
                client_status: t(
                  banned_at
                    ? 'worker.status.canPlay'
                    : 'worker.status.canPlay.no'
                ),
                disabled_at,
                banned_at,
              }),
              Cell: ({
                value,
              }: CellProps<{
                worker_status: string;
                client_status: string;
                disabled_at: boolean | null;
                banned_at: boolean | null;
              }>) => (
                <div className={s.status}>
                  <Label
                    text={value.worker_status}
                    color={value.disabled_at ? 'red' : 'green'}
                    iconName={!value.disabled_at ? 'SuccessIcon' : 'ErrorIcon'}
                    rounded
                  />
                  <Label
                    text={value.client_status}
                    color={value.banned_at ? 'red' : 'green'}
                    iconName="ConsoleIcon"
                    rounded
                  />
                </div>
              ),
              width: 265,
              disableResizing: true,
            },
          ]
        : []),
      {
        Header: t('worker.creator'),
        accessor: ({ creator }) => (creator ? creator.nickname : '—'),
        Cell: ({ value }: CellProps<IWorker>) => <>{value}</>,
        width: 192,
        disableResizing: true,
      },
      {
        Header: t('createdAt'),
        accessor: 'created_at',
        Cell: ({ value }) => {
          const date = dayjs(value);
          return (
            <div className={s.creates}>
              <span>{date.format(`D MMM YYYY`)}</span>
              <span className={s.time}>{date.format('HH:mm')}</span>
            </div>
          );
        },
        width: 174,
        disableResizing: true,
      },
    ];

    return baseColumns;
  }, [t, size]);
};
